import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { CategoryDA } from '../../services/admin/category.service';
import { Error } from '../../models/error.model';
import { Company } from '../../models/company.model';
import { Category } from '../../models/category.model';
import { AccountingAccount } from '../../models/accounting.account.model';
import { GlobalSwal } from '../../styles/swal.style';
import { modalHide } from '../../utils/modal.util';
import { legendInvalid, legendIconInvalid, legendValidInvalidRestart, legendIconValidInvalidRestart } from '../../utils/legend.data.util';
import { isValidObjectAttributes } from '../../scripts/compare.object.script';
import { suffixLegend } from '../../scripts/name.legend.script';
import { errorWebHandler } from '../../scripts/error.web.handler.script';

import AppCategoryForm from '../../interfaces/main/inventory/category/component/CategoryForm';
import AppLoading from '../Loading';

export interface AppModalCategoryUpdateProps {
  companyForUser: Company | null,
  categoriesForCompany: Category[] | null,
  accountingExpenseAccountsForCompany: AccountingAccount[] | null,
  accountingVatAccountsForCompany: AccountingAccount[] | null,
  accountingIncomeAccountsForCompany: AccountingAccount[] | null,
  category: Category | null,
  categories?: Category[] | null,
  setCategoriesForCompany: Dispatch<SetStateAction<Category[] | null>>,
  setCategory: Dispatch<SetStateAction<Category | null>>,
  setCategories?: Dispatch<SetStateAction<Category[] | null>>
};

let errorResponse: Error, categoryResponse: Category;

const AppModalCategoryUpdate: React.FunctionComponent<AppModalCategoryUpdateProps> = ({companyForUser, categoriesForCompany, accountingExpenseAccountsForCompany, accountingVatAccountsForCompany, accountingIncomeAccountsForCompany, category, categories, setCategoriesForCompany, setCategory, setCategories}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [name, setName] = useState({value: '', valid: false})
  const [description, setDescription] = useState({value: '', valid: false})
  const [names, setNames] = useState({value: {en: '', fr: '', it: '', de: ''}, valid: {en: true, fr: true, it: true, de: true}})
  const [accounts, setAccounts] = useState({value: {expense: '', vat: '', income: ''}, valid: {expense: false, vat: false, income: false}})

  const submitCategoryUpdate = async () => {
    setLoadIndicator('on')

    let namesIsValid: boolean = isValidObjectAttributes(names.valid)
    let accountsIsValid: boolean = isValidObjectAttributes(accounts.valid)

    if (companyForUser && categoriesForCompany && category && name.valid && description.valid && namesIsValid && accountsIsValid) {
      await CategoryDA.putCategory(companyForUser.id, category.id, name.value, description.value, names.value, accounts.value).then( (response) => {
        if (response.status === 200) {
          categoryResponse = response.data

          let tempCategoriesForCompany = categoriesForCompany.map((temp_item) => {
            if (temp_item.id === category.id) {
              temp_item = categoryResponse
            }
            return temp_item
          })

          setCategoriesForCompany(tempCategoriesForCompany)

          if (categories && setCategories) {
            let tempCategories = categories.map((temp_item) => {
              if (temp_item.id === category.id) {
                temp_item = categoryResponse
              }
              return temp_item
            })
            setCategories(tempCategories)
          } else {
            setCategory(categoryResponse)
          }

          GlobalSwal.fire({
            title: lang.labels.successfullyUpdatedCategory,
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            executeHideModalCategoryUpdate()
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: `${errorResponse.message}!`,
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!name.value.length) legendIconInvalid('modal-category-update-input-name', 'modal-category-update-legend-name-required')
        if (!description.value.length) legendInvalid('modal-category-update-legend-description-required')
        if (!accounts.value.expense.length) legendInvalid('modal-category-update-legend-account-expense-required')
        if (!accounts.value.vat.length) legendInvalid('modal-category-update-legend-account-vat-required')
        if (!accounts.value.income.length) legendInvalid('modal-category-update-legend-account-income-required')
        setLoadIndicator('off')
      })
    }
  }

  const executeHideModalCategoryUpdate = () => {
    modalHide('modal-category-update')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    legendIconValidInvalidRestart('modal-category-update-input-name', 'modal-category-update-legend-name-valid')
    legendIconValidInvalidRestart('modal-category-update-input-name', 'modal-category-update-legend-name-required')
    legendValidInvalidRestart('modal-category-update-legend-description-valid')
    legendValidInvalidRestart('modal-category-update-legend-description-required')
    legendValidInvalidRestart('modal-category-update-legend-account-expense-required')
    legendValidInvalidRestart('modal-category-update-legend-account-vat-required')
    legendValidInvalidRestart('modal-category-update-legend-account-income-required')

    for (var index in Object.keys(names.valid)) {
      legendIconValidInvalidRestart(`modal-category-update-input-language-name${suffixLegend(index)}`, `modal-category-update-legend-language-name-valid${suffixLegend(index)}`)
    }

    if (categories && setCategories) {
      setCategory(null)
    }
  }

  useEffect( () => {
    if (category) {
      setName({value: category.display_name, valid: true})
      setDescription({value: category.description, valid: true})
      setNames({value: {en: category.names.en, fr: category.names.fr, it: category.names.it, de: category.names.de}, valid: {en: true, fr: true, it: true, de: true}})
      setAccounts({value: {expense: category.accounts.expense, vat: category.accounts.vat, income: category.accounts.income}, valid: {expense: true, vat: true, income: true}})
    } else {
      setName({value: '', valid: false})
      setDescription({value: '', valid: false})
      setNames({value: {en: '', fr: '', it: '', de: ''}, valid: {en: true, fr: true, it: true, de: true}})
      setAccounts({value: {expense: '', vat: '', income: ''}, valid: {expense: false, vat: false, income: false}})
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category])

  return (
    <div id="modal-category-update" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.updateCategory}</h2>
          </div>
          <div className="modal-body py-0">
            { companyForUser && accountingExpenseAccountsForCompany && accountingVatAccountsForCompany && accountingIncomeAccountsForCompany && category
              ?
              <AppCategoryForm component={"modal-category-update"} category={category} name={name} description={description} names={names} accounts={accounts} setName={setName} setDescription={setDescription} setNames={setNames} setAccounts={setAccounts}></AppCategoryForm>
              :
              <AppLoading></AppLoading>
            }
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalCategoryUpdate}>{lang.labels.discard}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitCategoryUpdate}>
              <span className="indicator-label">{lang.labels.update}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppModalCategoryUpdate;
